import React from 'react';
import { IonApp, IonContent, IonPage } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import Header from 'layouts/Header';
import './SecurityPage.css';

const SecurityPage = () => {
  return (
    <IonApp>
      <Helmet>
        <title>Security &amp; Trust - Zibby</title>
        <meta name="description" content="Security &amp; Trust at Zibby - Start with zero codebase access and grant more only when you're ready. Per-tenant isolation, short-lived credentials, self-host, and responsible vulnerability disclosure." />
      </Helmet>
      <IonPage>
        <Header user={null} />
        <IonContent className="security-ion-content">
          <div className="security-page">
      <div className="security-container">
        <header className="security-header">
          <h1>Security &amp; Trust</h1>
          <p className="security-tagline">Your code is yours. We're built so you never have to hand it over to get value.</p>
        </header>

        <div className="security-content">
          <section className="security-section">
            <h2>Start without giving us your codebase</h2>
            <p>
              Zibby runs AI agents on your errors, pull requests, and tickets. We designed the product so you
              can start with <strong>zero codebase access</strong> and grant more only when you're ready — and so
              that even when you do connect a repo, your credentials and code are handled under strict, auditable
              isolation.
            </p>
            <p>
              Most AI dev tools demand write access to your source on day one. That's a lot to ask of a vendor
              you just met. Zibby lets you choose how far to go, and move up only when you've seen the value:
            </p>

            <div className="security-table-wrap">
              <table className="security-table">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>What you connect</th>
                    <th>What Zibby does</th>
                    <th>What we touch</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>1 · Triage</strong></td>
                    <td>Sentry (read) + Slack/Lark</td>
                    <td>Classifies new errors on an auditable severity rubric and posts digests to your channel</td>
                    <td><strong>Never touches your code</strong></td>
                  </tr>
                  <tr>
                    <td><strong>2 · Read analysis</strong></td>
                    <td>+ a read-only repo token</td>
                    <td>Adds "likely this file / this line" root-cause context to alerts</td>
                    <td>Read-only; we never write</td>
                  </tr>
                  <tr>
                    <td><strong>3 · Auto-fix</strong></td>
                    <td>+ a repo write token</td>
                    <td>Clones, makes the smallest correct fix behind your test suite, and opens a <strong>PR for a human to review</strong></td>
                    <td>Writes a branch + PR — <strong>never merges</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              <strong>The default is Tier 1.</strong> Connecting a repo later is an upgrade, not a precondition.
              If a fix is requested without a connected repo, the agent stops and tells you — it never fails
              silently and never guesses.
            </p>
          </section>

          <section className="security-section">
            <h2>When you do connect a repo, here's how we protect it</h2>
            <p>These aren't aspirations — they're invariants enforced in the runtime:</p>
            <ul>
              <li>
                <strong>Per-tenant isolation.</strong> Every request is scoped to the owning account and
                ownership-checked before any resource is read, changed, or returned. There is no shared "god"
                credential across tenants.
              </li>
              <li>
                <strong>Short-lived credentials, never persisted in the agent.</strong> The agent does the
                clone/push deterministically, then <strong>scrubs the token</strong> from its environment and
                from <code>.git/config</code>. A token never lingers where later steps (which read untrusted
                PR/issue content) could reach it.
              </li>
              <li>
                <strong>Untrusted input is treated as hostile.</strong> Agents read third-party content — PR
                diffs, stack traces, ticket text authored by people outside your team. Auto-write actions driven
                by that content are gated (human-in-the-loop / never self-approve / deterministic PR bodies) so a
                malicious payload can't steer the agent into acting on your behalf.
              </li>
              <li>
                <strong>No secret ever reaches logs, responses, or the model.</strong> Project secrets are masked
                on every read; live tokens are stripped from anything the model sees.
              </li>
              <li>
                <strong>SSRF-hardened.</strong> Any URL derived from user input is DNS-resolved and rejected if it
                points at a private/metadata address — string checks alone aren't enough, so we don't rely on them.
              </li>
              <li>
                <strong>Webhooks fail closed.</strong> Every inbound webhook verifies the provider's signature
                (timing-safe, with a replay window) before doing anything, and refuses to run if its secret isn't
                configured.
              </li>
              <li>
                <strong>We never auto-merge.</strong> Fixes land as pull/merge requests for your team to review
                and merge. The human stays in control of what ships.
              </li>
            </ul>
          </section>

          <section className="security-section">
            <h2>Prefer we never see your code at all? Self-host.</h2>
            <p>
              Zibby can run <strong>entirely inside your own infrastructure</strong>. In self-host mode your
              source, secrets, and error data never leave your environment — Zibby is the control plane, but the
              data plane is yours. This is the strongest answer for teams with strict data-residency or "nothing
              leaves our VPC" requirements.
            </p>
          </section>

          <section className="security-section">
            <h2>What we store, and for how long</h2>
            <ul>
              <li>
                <strong>Triage:</strong> error metadata and severity classifications needed to produce your
                digests. No source code is stored to run triage.
              </li>
              <li>
                <strong>Auto-fix:</strong> repositories are cloned into an <strong>ephemeral</strong> run
                workspace and discarded when the run ends. We don't retain a copy of your codebase.
              </li>
              <li>
                <strong>Secrets</strong> (integration tokens) are encrypted and scoped per project; they're used
                to authenticate a run and are never returned to the model or logged.
              </li>
            </ul>
            <p className="security-note">
              Exact retention windows and a data-processing addendum are available on request — see below.
            </p>
          </section>

          <section className="security-section">
            <h2>Reporting a vulnerability</h2>
            <p>
              Found a security issue? We want to hear about it. Email{' '}
              <a href="mailto:security@zibby.dev">security@zibby.dev</a> with details and steps to reproduce. We
              aim to acknowledge within <strong>2 business days</strong> and will keep you updated as we
              investigate. Please give us a reasonable window to remediate before any public disclosure.
            </p>
          </section>

          <section className="security-section">
            <h2>Compliance &amp; roadmap</h2>
            <ul>
              <li>
                <strong>Self-host today</strong> for teams that need data to stay in their own environment.
              </li>
              <li>
                <strong>SOC 2</strong> is on our roadmap; we're happy to share our current security posture,
                answer a security questionnaire, or sign a DPA/NDA for evaluation. Reach us at{' '}
                <a href="mailto:security@zibby.dev">security@zibby.dev</a>.
              </li>
            </ul>
            <p className="security-note">
              We list only what's real. We'll update this section as certifications complete — we won't claim one
              we don't hold.
            </p>
          </section>
        </div>
      </div>
          </div>
        </IonContent>
      </IonPage>
    </IonApp>
  );
};

export default SecurityPage;
